import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, CardText } from 'reactstrap';
import useGetArticleBySlug from '../../api/useGetArticleBySlug';
import HtmlComponent from '../shared/HtmlComponent';
import DisplayQueryError from '../shared/DisplayQueryError';
import LoadingSpinner from '../shared/LoadingSpinner';
import DocumentHead from '../shared/DocumentHead';
import Header from '../shared/Header';
import FeedbackBtn from '../shared/FeedbackBtn';
import { ArticleMetricType } from '../../types';
import { onGiveArticleFeedback } from '../../lib/feedback';
import useTrackPageView, {
    useTrackArticleView
} from '../../hooks/useTrackPageView.ts';
import usePagesUserId from '../../hooks/usePagesUserId.ts';

interface ArticleProps {
    slug: string;
    from: string;
}

const Article: React.FC<ArticleProps> = ({ slug, from }) => {
    useTrackPageView();
    useTrackArticleView(slug, from);
    const pagesUserId = usePagesUserId();

    const getArticleBySlug = useGetArticleBySlug({ slug });

    const {
        data: article,
        isLoading,
        error,
        refetch
    } = useQuery(['articleBySlug', slug], getArticleBySlug);

    if (error) {
        return <DisplayQueryError error={error} refetch={refetch} />;
    }

    if (isLoading || !article) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <DocumentHead title={' - ' + article.title} />
            <Header headerClassName='mini' />
            <section className='white-bg py-5'>
                <Container>
                    <Row>
                        <Col xs={12}>
                            <Card className='custom-card answer-card article-card mt-3'>
                                <CardBody className='custom-card-body'>
                                    <div className='d-flex justify-content-between align-items-center'>
                                        <h1 className='pr-3 mb-0'>
                                            {article.title}
                                        </h1>
                                    </div>
                                    <hr />
                                    <CardText className='mt-3'>
                                        <HtmlComponent html={article.content} />
                                    </CardText>
                                </CardBody>
                                <hr />
                                <Row>
                                    <Col xs={12}>
                                        <div className='align-self-start'>
                                            {article.tags?.map((tag) => (
                                                <Link
                                                    key={tag}
                                                    className='btn btn-outline-primary btn-pill m-1 px-2 py-1'
                                                    to={`/topic/${encodeURIComponent(
                                                        tag
                                                    )}`}
                                                >
                                                    <small>{tag}</small>
                                                </Link>
                                            ))}
                                        </div>
                                    </Col>
                                </Row>
                                <div className='d-flex flex-column justify-content-center align-items-center mt-3'>
                                    <h5>Helpful?</h5>
                                    <div>
                                        <FeedbackBtn
                                            sessionKey={`${article.articleId}-interaction`}
                                            good
                                            onClick={() =>
                                                onGiveArticleFeedback(
                                                    pagesUserId,
                                                    article.articleId,
                                                    ArticleMetricType.FeedbackGood
                                                )
                                            }
                                        />
                                        <FeedbackBtn
                                            sessionKey={`${article.articleId}-interaction`}
                                            onClick={() =>
                                                onGiveArticleFeedback(
                                                    pagesUserId,
                                                    article.articleId,
                                                    ArticleMetricType.FeedbackBad
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>
        </>
    );
};

export default Article;
